import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, defer, finalize, from, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { ApiResponse, UserAuth, UserAuthResponse } from '../../../types/commons';
import { DevLoginRequestDto, DevLoginResponseDto } from '../../../types/dev-auth';
import { Router } from '@angular/router';
import { ResetPasswordRequest } from '../../../types/reset-password';
import { ChangePasswordRequest } from '../../../types/profile';
import { ConversionValidationCacheService } from '../mbrs/conversion-validation-cache.service';
import { FilingFormPersistenceService } from '../mbrs/filing-form-persistence.service';
import { FilingSessionStore } from '../mbrs/filing-session-store.service';
import { DevSessionService } from '../dev/dev-session.service';
import { HttpStatus } from '../../utils/http-status.util';
import { UserRole, parseUserRole } from '../../../types/user-role';
import { roleCanManageUsers, roleCanUseConversions, homeUrlFor } from '../../utils/user-role.util';
import { encryptDevToken } from '../../utils/dev-token-encryptor';
import { clearCustomerDataCache } from '../../utils/customer-cache.util';
import {
  GENERIC_ERROR_MESSAGE,
  resolveApiErrorMessages,
} from '../../utils/api-error-messages.util';
import { formatLoadDiagnosticCode, formatToastDiagnosticCode, LOAD_FAILURE_COPY } from '../../utils/sheet-load-trace.util';
import type { DiagnosticScope } from '../../utils/navigation-diagnostic-scope.util';
import { RuntimeConfigService } from '../common/runtime-config.service';
import {
  CrossTabAuthScope,
  CrossTabAuthSyncService,
} from './cross-tab-auth-sync.service';
import { AuthFailureNoticeService } from './auth-failure-notice.service';
import { SessionEndDiagnosticsService } from './session-end-diagnostics.service';
import { SessionHintService } from './session-hint.service';
import { SessionTimingService } from './session-timing.service';

/**
 * Reasons a session can be torn down out from under the UI. Passed by
 * {@link AuthService.forceLogout} as a marker on the `/auth/login` navigation
 * state — read by {@link redirectIfAuthenticatedGuard} to recognise an
 * authoritative forced logout (AS-569) and skip the backend re-check.
 *
 * - `session-superseded` / `session-expired` are server-driven
 *   (single-active-session-per-device).
 * - `logged-out` is client-driven: another tab of the SAME session scope ran a
 *   voluntary logout and broadcast it via {@link CrossTabAuthSyncService}.
 * - `scope-switched` is client-driven cross-scope: another tab signed in to the
 *   *other* auth area (e.g. the developer portal) on this browser. The customer
 *   and dev areas are mutually exclusive per browser, so signing in to one
 *   tears the other's tabs down. Broadcast via {@link CrossTabAuthSyncService}
 *   `login$` (the dev side tears down silently, matching its voluntary-logout
 *   receiver).
 *
 * **Retired 2026-10-05 (P.O.): login-screen copy is no longer keyed by this
 * type.** A `FORCED_LOGOUT_LOGIN_MESSAGES` map used to live here, surfacing a
 * message for a natural `session-expired` only and staying silent for every
 * other reason — including `session-superseded` (signed in elsewhere, e.g. a
 * second browser), which left the FIRST browser showing nothing at all. The
 * login screen now shows an AUTH toast whenever a covered page/sheet load
 * actually classified its failing request as `AUTH`, carrying that request's
 * diagnostic code — see {@link AuthFailureNoticeService} and `LoginComponent`.
 * That is keyed by HTTP classification, not by this type, so this type is now
 * read only by {@link redirectIfAuthenticatedGuard} and the one-shot
 * `history.state` stripping in `LoginComponent`'s constructor.
 */
export type ForcedLogoutReason =
  | 'session-superseded'
  | 'session-expired'
  | 'logged-out'
  | 'scope-switched';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly config = inject(RuntimeConfigService);
  private readonly API_URL = `${this.config.apiUrl()}/auth`;
  /** `UserController` base — authenticated self-service routes that must carry the CSRF token. */
  private readonly USER_API_URL = `${this.config.apiUrl()}/user`;

  isAuthenticated = signal<boolean | null>(null);
  isAdmin = signal<boolean | null>(null);
  isDev = signal<boolean | null>(null);

  /**
   * The signed-in customer-area user's role, from `is-authenticated`'s `role`.
   * `null` = not signed in, a dev session, or a role this build does not know.
   * `isAdmin` stays for compatibility but is `true` for Admin ONLY, so it can no
   * longer stand in for "may manage users" — read the computeds below instead.
   */
  role = signal<UserRole | null>(null);
  readonly canManageUsers = computed(() => roleCanManageUsers(this.role()));
  readonly canUseConversions = computed(() => roleCanUseConversions(this.role()));
  readonly homeUrl = computed(() => homeUrlFor(this.role()));
  csrfToken = signal<string | null>(null);
  /**
   * Three-state verdict on the password-reset link this app lifetime last saw,
   * written by {@link isTokenValidGuard} and read by
   * `ResetPasswordStatusComponent` as its fallback when no navigation state is
   * available.
   *
   * `null` means "nothing to report" and is the ONLY safe default. It was
   * previously `signal<boolean>(false)`, which made the component's
   * `fallbackStatus !== null` check dead code and turned every loss of
   * navigation state into an unconditional "Password Reset Failed" screen —
   * including after a genuinely successful reset. The guard clears this back to
   * `null` on its success branch so a stale failure from an earlier expired link
   * cannot leak into a later, unrelated visit; these are root-provided signals
   * that otherwise live for the lifetime of the app.
   *
   * It is never set to `true`: a valid token means the reset form renders, and
   * the outcome of the reset itself travels in navigation state, not here.
   */
  isTokenValid = signal<boolean | null>(null);
  isTokenValidError = signal<string | null>(null);
  /** Collapses a burst of simultaneous 401s into a single forced logout. */
  private forcedLogoutInProgress = false;

  router = inject(Router);
  private validationCache = inject(ConversionValidationCacheService);
  private readonly filingPersistence = inject(FilingFormPersistenceService);
  /**
   * Injected purely so {@link wipeCustomerResidue} can clear the in-flight
   * filing-session id. `clearCustomerDataCache()` removes the `filingSessionId`
   * key from `sessionStorage`, but `FilingSessionStore` answers `get()` from an
   * in-memory SIGNAL seeded once at construction — so a storage-only sweep
   * leaves the id readable and the next user inherits it. Clearing it through
   * the owning service closes that gap for every teardown path at once.
   */
  private readonly filingSessionStore = inject(FilingSessionStore);
  private devSession = inject(DevSessionService);
  private crossTabAuth = inject(CrossTabAuthSyncService);
  private diagnostics = inject(SessionEndDiagnosticsService);
  private authFailureNotice = inject(AuthFailureNoticeService);
  /** TEMPORARY diagnostic: records how long each session lasts (non-prod only). */
  private sessionTiming = inject(SessionTimingService);
  private sessionHint = inject(SessionHintService);

  constructor(private http: HttpClient) {}

  getCsrfToken(): Observable<ApiResponse> {
    return this.http
      .get<ApiResponse>(`${this.API_URL}/csrf-token`, { withCredentials: true })
      .pipe(
        tap((response) => {
          if (response.status && response.data) {
            this.csrfToken.set(response.data.token);
          }
          else {
            this.csrfToken.set(null);
          }
        }),
        catchError(this.handleError));
  }

  validateResetToken(credentials: { email : string; token: string}): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${this.API_URL}/valid-token`, credentials, { withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  /**
   * Bootstrap auth probe — `GET /api/auth/is-authenticated`. The route guards
   * call this to decide whether to admit a user or send them to a login page.
   *
   * CONTRACT: a value is emitted ONLY when the backend gives an AUTHORITATIVE,
   * well-formed JSON answer (a boolean `isAuthenticated`). Any other outcome —
   * a transport failure, OR an HTTP 200 whose body is not the expected JSON
   * shape — is surfaced as an ERROR, and the auth signals are left UNTOUCHED.
   *
   * Why this matters (the duplicated-tab false-logout bug): a local dev-server
   * can answer this probe with the SPA's `index.html` (HTTP 200, HTML body)
   * when an `/api` request falls through its history-API fallback — reliably
   * reproducible by duplicating a tab. Angular surfaces that JSON parse failure
   * as an `HttpErrorResponse` with `status: 200`. The previous implementation
   * swallowed every failure into `{ isAuthenticated: false }`, which the guards
   * read as an authoritative "logged out" and bounced a perfectly valid session
   * to `/auth/login` (and dev sessions to the dev login). A failed probe is NOT
   * proof of logout — so we now rethrow and let the CALLER treat it as
   * "couldn't verify" (the guards route to `/error`), never as "logged out".
   * The remote nginx config proxies `/api` to the backend with a dedicated
   * `location`, so it never serves HTML here — which is why this only ever bit
   * locally.
   *
   * A teardown ({@link clearAuthState}) that lands while the request is in
   * flight (the SignalR `ForceLogout` push, a cross-tab logout) wins: the late
   * answer still emits, but does not re-set the signals on top of the wipe.
   */
  ensureUserAuthenticated(): Observable<UserAuth> {
    return defer(() => {
      const teardownCountAtStart = this.authTeardownCount;
      return this.http.get<UserAuthResponse>(`${this.API_URL}/is-authenticated`, {
        withCredentials: true,
      })
      .pipe(
        map((response) => {
          const data = response?.data;
          // A trustworthy answer requires the expected JSON shape. A non-JSON
          // 200 (e.g. index.html) arrives here as an HttpErrorResponse and skips
          // straight to catchError; this guards the subtler case of a 200 whose
          // body parsed but is the wrong shape (null / missing isAuthenticated).
          if (!data || typeof data.isAuthenticated !== 'boolean') {
            throw new Error(
              'is-authenticated returned a malformed body (expected JSON with a boolean isAuthenticated).'
            );
          }
          return data;
        }),
        tap((data) => {
          // Signals are written ONLY on an authoritative answer — never on the
          // failure path, so a bad probe cannot strand the UI in a false
          // logged-out state. Nor after a teardown ran mid-flight.
          if (this.authTeardownCount !== teardownCountAtStart)
            return;
          this.isAuthenticated.set(data.isAuthenticated);
          this.isAdmin.set(data.isAdmin);
          this.isDev.set(data.isDev ?? false);
          this.role.set(parseUserRole(data.role));
        }),
        catchError((error: unknown) => {
          if (!environment.production) {
            console.warn(
              '[Auth] is-authenticated probe failed; treating as "could not verify", NOT as logout:',
              error
            );
          }
          return throwError(() => error);
        })
      );
    });
  }

  /**
   * Bumped by every {@link clearAuthState}, so an in-flight
   * {@link ensureUserAuthenticated} can tell a teardown ran during its round
   * trip and leave the wiped signals alone.
   */
  private authTeardownCount = 0;

  /**
   * Read-only view of the teardown counter. A caller that awaits a session
   * check captures it before the await and compares after: a change means a
   * logout (SignalR `ForceLogout` push, cross-tab) ran mid-flight and already
   * owns the navigation. The `isAuthenticated` signal cannot tell that apart,
   * because an authoritative "signed out" answer also flips it to `false`.
   */
  get authTeardownGeneration(): number {
    return this.authTeardownCount;
  }

  /** The in-flight `verifySession()` request, shared until it settles. */
  private sessionCheck$: Observable<UserAuth> | null = null;

  /**
   * Pre-navigation session check — `isAuthenticatedGuard` calls this on EVERY
   * page navigation under `/mbrs`, so a session that ended mid-use sends the
   * user to login before the destination page loads (P.O., 2026-10-08).
   *
   * Same contract as {@link ensureUserAuthenticated} (it IS that request): an
   * authoritative answer emits, anything else errors. The only addition is
   * de-duplication of OVERLAPPING callers, such as rapid sheet switches. It
   * does not apply on entry into `/mbrs`: the router runs the parent's
   * `canActivate` and the child's `canActivateChild` one after the other, so
   * entry costs two sequential checks, and every later page change one.
   *
   * Deliberately NOT a time-based cache: once the request settles, the next
   * navigation asks again, so there is no window in which a dead session can
   * still load a page. And never call this on a timer. With sliding expiry on,
   * any periodic authenticated request would keep an idle tab signed in
   * forever. It must only ever follow a user's own navigation.
   */
  verifySession(): Observable<UserAuth> {
    this.sessionCheck$ ??= this.ensureUserAuthenticated().pipe(
      finalize(() => {
        this.sessionCheck$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.sessionCheck$;
  }

  /**
   * Dev-area login (v2.1 — JWT in response body, token encrypted on the wire).
   *
   * Flow:
   *   1. RSA-OAEP-SHA256 encrypt the plaintext token against
   *      `RuntimeConfigService.devAuthPublicKey()` (base64 SPKI of the
   *      dev JWT public key, loaded from `/assets/config.json` at boot).
   *      Ciphertext is re-generated on every submit — OAEP uses
   *      fresh randomness, so the wire payload never repeats.
   *   2. POST `{ encryptedToken }` to `/api/auth/dev-login`.
   *   3. On success, stash the returned JWT + expiry in `DevSessionService`
   *      so the `devJwtInterceptor` can attach `Authorization: Bearer` on
   *      subsequent dev requests. CSRF is skipped for the whole dev flow.
   *   4. Call `ensureUserAuthenticated()` so the `isDev` signal reflects
   *      the server's view rather than the optimistic payload.
   *
   * Any encryption failure (empty public key, malformed SPKI, Web Crypto
   * unavailable) is surfaced as a neutral "Invalid token" envelope, matching
   * the backend's flat 401 shape so no decryption oracle leaks.
   */
  devLogin(plaintextToken: string): Observable<ApiResponse<DevLoginResponseDto>> {
    return from(encryptDevToken(plaintextToken, this.config.devAuthPublicKey())).pipe(
      switchMap((encryptedToken) => {
        const body: DevLoginRequestDto = { encryptedToken };

        return this.http.post<ApiResponse<DevLoginResponseDto>>(`${this.API_URL}/dev-login`, body);
      }),
      switchMap((response) => {
        if (response.status && response.data?.token) {
          // DevSessionService.setSession handles customer-cache cleanup
          // so the login / logout / expiry paths all go through one
          // symmetric code path. See dev-session.service.ts.
          this.devSession.setSession(response.data.token, response.data.expiresAtUtc);
          
          // Only whoami runs here. We deliberately do NOT call
          // getCsrfToken() in the dev flow: `/api/auth/csrf-token`
          // requires a cookie-authenticated session which a Bearer-only
          // dev session does not have, so fetching it just 401s and
          // pollutes the console. Dev mutation endpoints that trip
          // ASP.NET Core antiforgery need `[IgnoreAntiforgeryToken]`
          // on the backend controller (Bearer auth is not CSRF-
          // vulnerable).
          return this.ensureUserAuthenticated().pipe(map(() => response));
        }
        return of(response);
      }),
      tap((response) => {
        if (response.status && environment.devAreaSlug) {
          // TEMPORARY diagnostic: stamp the dev session start. No-op in prod.
          this.sessionTiming.markLogin('dev');
          this.router.navigate([`dev-${environment.devAreaSlug}`, 'logs', 'api-audit']);
          // Cross-tab login broadcast (dev scope) — symmetric to the customer
          // login() path. Other dev tabs on the dev login page reload and let
          // redirectIfAuthenticatedGuard forward them into the dev area.
          this.crossTabAuth.broadcastLogin('dev');
        }
      }),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse)
          return this.handleDevLoginError(error);

        // Encryption path failure (empty / malformed public key, no Web
        // Crypto, plaintext > 190 bytes, etc.). Log for devs, neutral
        // envelope for the user.
        if (!environment.production)
          console.error('[Dev Login] Token encryption failed:', error);

        return of<ApiResponse<DevLoginResponseDto>>({
          status: false,
          statusCode: HttpStatus.Unauthorized,
          data: null as unknown as DevLoginResponseDto,
          errors: ['Invalid token'],
        });
      })
    );
  }

  private handleDevLoginError(error: HttpErrorResponse): Observable<ApiResponse<DevLoginResponseDto>> {
    if (!environment.production) {
      console.error('Dev Login Error:', {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
      });
    }

    if (error.status === HttpStatus.NetworkError)
      throw error;
    
    const body = error.error;
    const errors: string[] = 
      Array.isArray(body?.errors) ? body.errors : 
      (body?.message ? [body.message] : ['Unknown error occurred']);
    
    return of({
      status: false,
      statusCode: error.status || HttpStatus.InternalServerError,
      data: null as unknown as DevLoginResponseDto,
      errors,
    });
  }

  login(credentials: { email: string; password: string }): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.API_URL}/login`, credentials, {
        withCredentials: true,
      })
      .pipe(
        switchMap((response) => {
          if (response.status) {
            // Defensive wipe on the way IN. Every other call site of
            // `wipeCustomerResidue` is a teardown, and relying on teardown alone
            // means one missed logout path is enough to hand a user the previous
            // user's data — which is exactly what a failed `POST /auth/logout`
            // used to do (its caller redirects to `/auth/login` either way).
            //
            // Placed before `ensureUserAuthenticated` so nothing this login goes
            // on to populate can be caught by it. Cheap on the normal path: a
            // clean logout already emptied all four stores, so this is a no-op.
            //
            // Accepted cost: a user who signs back in on a tab whose logout
            // FAILED loses filing drafts that failed teardown had left behind.
            // That matches the contract every successful logout already
            // enforces — a login starts clean — and losing your own draft is
            // strictly better than inheriting somebody else's session.
            this.wipeCustomerResidue();
            // Sequential: auth check first (AllowAnonymous), then CSRF token
            // This ensures the auth cookie is fully processed before the CSRF request
            return this.ensureUserAuthenticated().pipe(
              switchMap(() => this.getCsrfToken()),
              map(() => response)
            );
          }
          else {
            // login failed, emit response immediately
            return of(response);
          }
        }),
        tap((response) => {
          if (response.status) {
            // TEMPORARY diagnostic: stamp the session start so we can measure
            // how long it lasts when it ends (logout / forced logout). No-op in
            // production. See SessionTimingService.
            this.sessionTiming.markLogin('customer');

            // Remember that this browser now holds a customer session, so a
            // revisit after it expires (cookie gone) can still toast MBRS-AUTH
            // once. Not auth state; see SessionHintService.
            this.sessionHint.markSessionExists();

            // navigate
            this.router.navigateByUrl(this.homeUrl());
            
            // Cross-tab login broadcast. Tell every OTHER customer tab sitting
            // on a login page to follow this sign-in into the app. Fired only
            // on the SUCCESS path (unlike logout's `finalize`) — a failed login
            // must never tell other tabs to navigate. By now the auth cookie is
            // live (ensureUserAuthenticated already used it above), so the
            // receiver's reload → redirectIfAuthenticatedGuard will see it.
            this.crossTabAuth.broadcastLogin('customer');
            // ----------------------
            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // console.log("[Auth Service] Broadcast Customer Login.");
            // ----------------------
          }
        }),
        // Unlike the other auth calls, a failed login keeps the body's `data`:
        // the 429 lockout carries `data.lockoutMinutes`, which the login form
        // puts in its message (`resolveLoginFailure`). `handleError` stays
        // `data: null` for every other endpoint. A network error (status 0)
        // is still re-thrown by `handleError` before this map runs.
        catchError((error: HttpErrorResponse) =>
          this.handleError(error).pipe(
            map((envelope) => ({ ...envelope, data: error.error?.data ?? null }))
          )
        )
      );
  }

  logout(): Observable<ApiResponse> {
    // Capture the scope NOW, before `tearDownLocally` runs `clearAuthState()`
    // and flips `isDev` to false — otherwise a dev logout would broadcast as
    // 'customer'.
    const scope: CrossTabAuthScope = this.isDev() ? 'dev' : 'customer';

    // Local teardown for a voluntary sign-out. Runs on BOTH arms of the tap
    // below, and that is the whole point: `UserSignoutComponent.logout()`
    // navigates to `/auth/login` from its `next` AND its `error` handler, so a
    // teardown wired only to the success path leaves the previous user's session
    // id, validation cache, drafts and CSRF token resident in a tab that is now
    // showing a login screen to somebody else. The user asked to leave; whether
    // the server acknowledged is not our business locally.
    //
    // Both halves are idempotent (`markEnd` by its own contract,
    // `clearAuthState` because every step is a set-to-empty).
    const tearDownLocally = () => {
      // TEMPORARY diagnostic: close the timing record for a voluntary,
      // same-tab logout before the state is wiped. No-op in prod.
      this.sessionTiming.markEnd('manual-logout');
      this.clearAuthState();
    };

    return this.http
      .post<ApiResponse>(
        `${this.API_URL}/logout`,
        {},
        {
          withCredentials: true,
        }
      )
      .pipe(
        // This `tap` stays BEFORE `catchError`, on both counts:
        //   - its `error` handler still sees the raw `HttpErrorResponse`, which
        //     `handleError` is about to convert into a `next` emission of a
        //     `{ status: false }` envelope. After the conversion a failed logout
        //     is indistinguishable from a successful one, so this is the last
        //     point at which the failure arm can be recognised at all.
        //   - it keeps the teardown SYNCHRONOUSLY ahead of the subscriber's own
        //     `router.navigate(['/auth/login'])`. That order is load-bearing:
        //     `canDeactivateFilingGuard` short-circuits on
        //     `isAuthenticated() === false`, so deferring the teardown to
        //     `finalize` would let the "unsaved changes" prompt block a sign-out
        //     off /mbrs/filing.
        tap({ next: tearDownLocally, error: tearDownLocally }),
        catchError(this.handleError),
        // Cross-tab logout broadcast. Placed AFTER catchError so it fires on
        // BOTH paths: the success path AND the error path (a dev logout may
        // 401 — dev uses a Bearer token, not the auth cookie — yet the other
        // dev tabs must still be signed out). This is the ONLY broadcast site:
        // receivers tear down via forceLogout()/clearAuthState(), which never
        // re-broadcast, so there is zero echo-storm risk.
        finalize(() => this.crossTabAuth.broadcastLogout(scope))
      );
  }

  clearAuthState(options?: { preserveDevSession?: boolean }): void {
    this.authTeardownCount++;
    this.isAuthenticated.set(false);
    this.isAdmin.set(false);
    this.isDev.set(false);
    this.role.set(null);
    this.csrfToken.set(null);
    // Every teardown ends the "a session may have lapsed unseen" window: a
    // manual logout needs no toast, and a forced logout has staged its own.
    this.sessionHint.clear();
    // A cookie sign-out should also drop the dev JWT so the two session
    // concepts never disagree. EXCEPTION: `preserveDevSession` — used by the
    // cross-SCOPE teardown when a DEV login displaces this customer tab. The
    // dev JWT lives in a browser-wide cookie (DevSessionService), so clearing
    // it here would delete the session the dev tab JUST created. The customer
    // tab must therefore tear down its own state while leaving dev untouched.
    if (!options?.preserveDevSession)
      this.devSession.clearSession();

    this.wipeCustomerResidue();
  }

  /**
   * Erases every trace of the current customer's data from this browser tab,
   * WITHOUT touching the auth signals or the dev session.
   *
   * Split out of {@link clearAuthState} because it is needed on the way IN as
   * well as on the way out — see {@link login}. Until this split, all session
   * hygiene hung off the logout path, so any logout that did not reach
   * `clearAuthState` (a failed `POST /auth/logout`, whose caller redirects to
   * `/auth/login` regardless) handed the whole lot to whoever logged in next.
   * A cross-user leak needs only one such path; running the wipe on both edges
   * means no single missed path is sufficient.
   *
   * Idempotent and safe to call repeatedly. Deliberately covers all four stores
   * customer data can hide in — a new one MUST be added here, not just to
   * `clearCustomerDataCache`:
   *
   *   1. the in-memory conversion-validation cache,
   *   2. the in-memory filing-session id signal,
   *   3. `localStorage` + `sessionStorage`,
   *   4. the filing-form IndexedDB database.
   */
  private wipeCustomerResidue(): void {
    // Drop every cached conversion validation status so the next signed-in
    // user never sees stale results from the previous session. Centralised
    // here so all logout paths (explicit logout, session expiry, forced
    // logout from SignalR user-deletion) trigger it consistently.
    this.validationCache.clearAll();
    // Drop the in-flight filing-session id. The `clearCustomerDataCache()` call
    // below already removes the `filingSessionId` STORAGE key, but that sweep is
    // DI-free and cannot reach `FilingSessionStore`'s in-memory signal — which is
    // what `get()` actually reads. Leaving the signal set is not cosmetic: the
    // next user's first upload would resume the PREVIOUS user's session id, and
    // the backend does not check session ownership, so their documents would be
    // uploaded into and submitted under the previous user's conversion. See
    // `FilingSessionUploader.resolveSession`.
    this.filingSessionStore.clear();
    // Wipe every customer-owned local/session storage entry so the next
    // user who logs in on this browser can't see the previous user's
    // filing state, pending updates, or per-sheet drafts. Applies to
    // voluntary customer logout, forced logout (SignalR user-deleted /
    // user-updated), dev sign-out, and dev session-expired flows.
    // See src/app/utils/customer-cache.util.ts for the key inventory.
    clearCustomerDataCache();
    // Wipe filing-form IndexedDB state on teardown (clearCustomerDataCache only
    // covers localStorage/sessionStorage). Fire-and-forget — logout must not
    // block on storage. Uses the persistence service's already-open IDB
    // connection (clearAll on the store), never deleteDB (which would block on
    // the open connection).
    void this.filingPersistence.clearAll();
  }

  /**
   * Tears the UI down locally after the session has been invalidated out from
   * under this tab. Triggered by:
   *   - the {@link sessionExpiryInterceptor} on a `session-superseded` / plain
   *     401 from any authenticated, non-dev API call (default OnNextApiCall mode),
   *   - the `ForceLogout` SignalR push from `UserHub` (InstantPush mode), or
   *   - a CROSS-TAB logout broadcast (client-side): another customer tab ran a
   *     voluntary logout and the {@link CrossTabAuthSyncService} receiver in
   *     `MainLayoutComponent` calls this with the `'logged-out'` reason. Unlike
   *     the first two, this trigger is not server-driven — the cookie was
   *     already cleared by the originating tab's `POST /auth/logout`.
   *   - a CROSS-SCOPE login broadcast (client-side): a DEV login on another tab
   *     displaces this customer tab (the two areas are mutually exclusive per
   *     browser). `MainLayoutComponent` calls this with `'scope-switched'` AND
   *     `{ preserveDevSession: true }` — the customer cookie is NOT dead here
   *     (no `POST /auth/logout` runs, same as the other triggers), and the dev
   *     JWT cookie must be left intact because the dev tab just created it.
   *     This is a UI-level teardown; the customer server session lingers until
   *     it expires or is explicitly ended.
   *
   * Unlike {@link logout}, this does NOT call `POST /auth/logout`: the cookie
   * is already dead server-side (the 401 / push is the proof), so a logout
   * call would just 401 again. We only wipe in-memory + customer-cache state
   * and bounce to `/auth/login`. The navigation carries the machine `reason` as
   * a forced-logout marker (so {@link redirectIfAuthenticatedGuard} skips its
   * backend re-check — AS-569).
   *
   * **For the two server-driven reasons** (`session-expired`,
   * `session-superseded`), this stages a fallback AUTH diagnostic code on
   * {@link AuthFailureNoticeService} before anything else, so the login page
   * always has SOMETHING to show — even for the InstantPush SignalR trigger
   * above, where no HTTP request ever failed and so nothing else would ever
   * stage one. Whichever page/sheet load actually classified the triggering
   * 401 (the OnNextApiCall trigger) stages its own, more specific code
   * moments later — synchronously, as the error re-throws back out of
   * `sessionExpiryInterceptor` — which safely overwrites this generic one.
   * Not staged for `logged-out`/`scope-switched`: both are client-driven with
   * no session genuinely compromised server-side, and stay silent by design.
   *
   * SignalR teardown is intentionally not done here. Navigating to
   * `/auth/login` unmounts `MainLayoutComponent`, whose `ngOnDestroy` closes
   * the `UserHub` and conversion-progress connections — keeping the hub
   * lifecycle owned by the layout that created it (DIP / SRP seam).
   *
   * Idempotent: when a session is superseded, every in-flight request can
   * 401 at once. The `forcedLogoutInProgress` guard collapses that burst into
   * a single redirect, and `clearAuthState()` flips `isAuthenticated` to false
   * so later 401s no longer pass the interceptor's gate.
   */
  forceLogout(
    reason: ForcedLogoutReason,
    options?: { preserveDevSession?: boolean; diagnosticScope?: DiagnosticScope },
  ): void {
    // [AS-569 debug] forceLogout entry trace — who triggered it, zone state, and
    // whether the burst-collapse guard short-circuited it. Uncomment (and re-add
    // `NgZone` to the @angular/core import) to re-enable. Dev only.
    // if (!environment.production) {
    //   console.log('[forceLogout] invoked', {
    //     reason,
    //     alreadyInProgress: this.forcedLogoutInProgress,
    //     inAngularZone: NgZone.isInAngularZone(),
    //     currentUrl: this.router.url,
    //     timestamp: new Date().toISOString(),
    //   });
    // }
    const routeAtEntry = this.router.url;
    if (this.forcedLogoutInProgress) {
      // Burst-collapse: every in-flight request can 401 at once; we only want
      // one redirect. Logged so a future incident shows the collapse happened
      // (and isn't mistaken for a dropped signal).
      this.diagnostics.record({
        source: 'force-logout',
        reason,
        route: routeAtEntry,
        action: 'already-in-progress',
      });
      return;
    }
    this.forcedLogoutInProgress = true;

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    console.log("[On Forced Logout] Reason: ", reason);

    // See this method's docblock. Placed after the burst-collapse latch so
    // only the one winning call stages anything. Composes the full toast
    // line — see AuthFailureNoticeService's docblock for why a bare code is
    // no longer enough — but AUTH is the one class this generic fallback can
    // safely assume, since it only fires from the two session-ended reasons.
    if (reason === 'session-expired' || reason === 'session-superseded') {
      // `diagnosticScope` names the page the pre-navigation gate caught the
      // user LEAVING; every other caller (401 interceptor, SignalR push,
      // cross-tab) passes none. The TOAST reads the same `MBRS-AUTH` either
      // way (AUTH is an identifier-free class); the console keeps the scoped
      // code so a developer can still tell which page the user was leaving.
      const scope = options?.diagnosticScope?.scope ?? 'SESSION';
      const conversionId = options?.diagnosticScope?.conversionId ?? null;
      console.error(`[auth] ${formatLoadDiagnosticCode(scope, 'AUTH', conversionId)}`, { reason });
      const toastCode = formatToastDiagnosticCode(scope, 'AUTH', conversionId);
      this.authFailureNotice.stage(`${LOAD_FAILURE_COPY.AUTH} ${toastCode}`);
    }

    // TEMPORARY diagnostic: close the timing record for this forced teardown.
    // `ForcedLogoutReason` is a subset of `SessionEndReason`, so the reason
    // passes through verbatim. Placed AFTER the burst-collapse latch so only the
    // winning 401 records the end (markEnd is also independently idempotent),
    // and BEFORE clearAuthState so it runs even if the wipe throws below. No-op
    // in production. See SessionTimingService.
    this.sessionTiming.markEnd(reason);

    // AS-569 (stuck-page regression): `clearAuthState` wipes localStorage and
    // the validation cache. If any of that THROWS (storage access denied in a
    // locked-down/private browser, a cache bug), the old code skipped both the
    // redirect AND the `finally` that releases this latch — stranding
    // `forcedLogoutInProgress = true` forever, so every later 401 short-circuited
    // and the user sat on a dead page. We now isolate the wipe so the redirect
    // (and the latch release below) ALWAYS run.
    try {
      this.clearAuthState({ preserveDevSession: options?.preserveDevSession });
    }
    catch (err) {
      if (!environment.production)
        console.error('[forceLogout] clearAuthState threw; redirecting anyway', err);
    }

    let navigation: Promise<boolean>;
    try {
      navigation = this.router.navigate(['/auth/login'], {
        // Marker only: a truthy `authReason` tells redirectIfAuthenticatedGuard
        // this navigation is an authoritative forced logout (AS-569), and
        // LoginComponent strips it one-shot. It carries the machine `reason`,
        // never user copy — the login screen's AUTH toast is driven by the
        // staged diagnostic code above, not by this marker.
        state: { authReason: reason },
      });
    }
    catch (err) {
      // A synchronous navigate failure must not deadlock the latch either.
      this.forcedLogoutInProgress = false;
      this.diagnostics.record({
        source: 'force-logout',
        reason,
        route: routeAtEntry,
        action: 'navigate-threw',
      });

      if (!environment.production)
        console.error('[forceLogout] router.navigate threw synchronously', err);
      
      return;
    }

    navigation
      .then((navigated) => {
        // Navigation outcome is the BE's "what did the FE do next" signal
        // (AS-569 handoff §3). `landedUrl !== /auth/login` would mean a guard
        // bounced us back and silently undid the logout.
        this.diagnostics.record({
          source: 'force-logout',
          reason,
          route: routeAtEntry,
          action: 'redirect-to-login',
          navigated,
          landedUrl: this.router.url,
        });
      })
      .catch((err) => {
        this.diagnostics.record({
          source: 'force-logout',
          reason,
          route: routeAtEntry,
          action: 'navigation-rejected',
        });
        if (!environment.production) {
          console.error('[forceLogout] navigation to /auth/login rejected', err);
        }
      })
      .finally(() => {
        this.forcedLogoutInProgress = false;
      });
  }

  forgotPassword(credentials: {email: string}): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.API_URL}/forgot-password`, credentials, { withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  resetPassword(credentials: ResetPasswordRequest) : Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.API_URL}/reset-password`, credentials, { withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  /**
   * Self-service password change (`POST /api/user/change-password`). Any
   * authenticated user may call it — including a User-role account (whose
   * name/email/company/role are Admin-controlled and must go through
   * `PUT /api/user/update-user`) and a SuperAdmin.
   *
   * Why it lives under `/user/` and not `/auth/`: `csrfInterceptor` skips
   * everything under `/api/auth/` (it is mostly anonymous), and the backend
   * answers a missing CSRF token with 401 — which reads like a dead session.
   * On `/api/user/` the token is attached like any other authenticated mutation.
   *
   * Returns a 400 envelope with `errors[]` for a wrong current password, a
   * confirm mismatch, or a complexity-policy failure.
   *
   * A successful change **keeps** the caller signed in — only a role change
   * ends a session. So no sign-out handling belongs here, and a caller may
   * safely make further requests on the same session straight afterwards.
   */
  changePassword(credentials: ChangePasswordRequest): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.USER_API_URL}/change-password`, credentials, { withCredentials: true })
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse): Observable<ApiResponse> {
    // Only log errors in development environment (prevents sensitive data exposure in production)
    if (!environment.production) {
      // Log sanitized error info (not full error object which may contain sensitive data)
      console.error('Auth Error:', {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
      });
    }

    // Re-throw network errors (status 0) so they can be handled by the global interceptor
    // This happens when the backend is completely unreachable
    if (error.status === HttpStatus.NetworkError) {
      throw error;
    }

    // Shared resolution: body-supplied messages first, then the role-refusal
    // message for a 403 (which arrives with an empty body), then the fallback.
    // This service never surfaces the raw transport string (e.g. "Http
    // failure response for ... 400 Bad Request") — the fallback passed here
    // is already fully resolved to the body's `message`, or else the generic
    // fallback text, so `resolveApiErrorMessages`'s own `error.message`
    // fallback is never reached.
    //
    // The envelope itself stays local: `data: null` is this service's shape.
    //
    // `errorCode` is copied across explicitly. This envelope is SYNTHESIZED,
    // so any body field not named here never reaches the caller — which would
    // make the login rate-limit (`LOGIN_TOO_MANY_ATTEMPTS`, see
    // `login-errors.util.ts`) undetectable, since `errorCode` is the only
    // stable signal that separates it from other 429s.
    const errorCode = error.error?.errorCode;
    const apiError: ApiResponse = {
      status: false,
      statusCode: error.status || HttpStatus.InternalServerError,
      data: null,
      errors: resolveApiErrorMessages(error, {
        fallbackMessage: error.error?.message ?? GENERIC_ERROR_MESSAGE,
      }),
      ...(typeof errorCode === 'string' && errorCode.length > 0 ? { errorCode } : {}),
    };

    return of(apiError);
  }
  
}
